import { useState, useEffect, useContext } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import toast from "react-hot-toast";
import { UserContext } from "../../UserContext";
import styles from "./BusinessEntityForm.module.css";
import { FaArrowLeft, FaSave, FaTimes } from "react-icons/fa";

const BusinessEntityForm = () => {
  const { t, i18n } = useTranslation();
  const { entityId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { userData, token } = useContext(UserContext);

  const isEditMode = !!entityId;
  const existingEntity = location.state?.entity;

  const [formData, setFormData] = useState({
    name: "",
    address: "",
    phone: "",
    email: "",
    accountType: "CLIENT",
    initialBalance: "",
    creditLimit: "",
    farmId: userData?.Farm?.id || null,
    tenantId: userData?.Tenant?.id || null,
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isEditMode && existingEntity) {
      setFormData({
        ...existingEntity,
        initialBalance: existingEntity.initialBalance?.toString() || "",
        creditLimit: existingEntity.creditLimit?.toString() || "",
      });
    }
  }, [isEditMode, existingEntity]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = t("Name is required");
    }

    if (!formData.phone.trim()) {
      newErrors.phone = t("Phone is required");
    } else if (!/^[+]?[\d\s()-]+$/.test(formData.phone)) {
      newErrors.phone = t("Invalid phone number format");
    }

    if (!formData.address.trim()) {
      newErrors.address = t("Address is required");
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t("Invalid email format");
    }

    if (formData.initialBalance && isNaN(formData.initialBalance)) {
      newErrors.initialBalance = t("Initial balance must be a number");
    }

    if (formData.creditLimit && isNaN(formData.creditLimit)) {
      newErrors.creditLimit = t("Credit limit must be a number");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleAccountTypeChange = (type) => {
    setFormData((prev) => ({
      ...prev,
      accountType: type,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    // Only send the fields that the API expects (matching Android implementation)
    const entityData = {
      farmId: formData.farmId || userData?.Farm?.id,
      tenantId: formData.tenantId || userData?.Tenant?.id,
      name: formData.name,
      address: formData.address,
      email: formData.email || null,
      accountType: formData.accountType.toLowerCase(), // Convert to lowercase to match Android
      phone: formData.phone,
      initialBalance: parseFloat(formData.initialBalance) || 0,
      creditLimit: parseFloat(formData.creditLimit) || 0,
    };

    try {
      if (isEditMode) {
        // Update existing entity using the unified API endpoint
        // Use the entity's accountId in the URL, not the regular id
        const updateId = existingEntity?.accountId || entityId;
        await axios.put(
          `api/entitiesAccounts/updateEntityAccount/${updateId}`,
          entityData,
          { headers: { Authorization: token } }
        );
        toast.success(t("Entity updated successfully"));
      } else {
        // Create new entity using the unified API endpoint
        await axios.post(
          `api/entitiesAccounts/createEntityAccount`,
          entityData,
          { headers: { Authorization: token } }
        );
        toast.success(t("Entity added successfully"));
      }
      navigate("/business-entities");
    } catch (error) {
      console.error("Error saving entity:", error);
      toast.error(
        isEditMode ? t("Error updating entity") : t("Error adding entity")
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/business-entities");
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <button
          className={styles.backButton}
          onClick={() => navigate("/business-entities")}
        >
          <FaArrowLeft />
        </button>
        <h1>
          {isEditMode
            ? t("Edit Business Entity")
            : t("Add New Business Entity")}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className={styles.form}>
        <div className={styles.card}>
          {/* Entity Type Selection */}
          <div className={styles.formSection}>
            <label className={styles.sectionLabel}>{t("Entity Type")}</label>
            <div className={styles.radioGroup}>
              <label className={styles.radioLabel}>
                <input
                  type="radio"
                  name="accountType"
                  value="CLIENT"
                  checked={formData.accountType === "CLIENT"}
                  onChange={() => handleAccountTypeChange("CLIENT")}
                  className={styles.radioInput}
                />
                <span className={styles.radioText}>{t("Client")}</span>
              </label>
              <label className={styles.radioLabel}>
                <input
                  type="radio"
                  name="accountType"
                  value="SUPPLIER"
                  checked={formData.accountType === "SUPPLIER"}
                  onChange={() => handleAccountTypeChange("SUPPLIER")}
                  className={styles.radioInput}
                />
                <span className={styles.radioText}>{t("Supplier")}</span>
              </label>
              <label className={styles.radioLabel}>
                <input
                  type="radio"
                  name="accountType"
                  value="BOTH"
                  checked={formData.accountType === "BOTH"}
                  onChange={() => handleAccountTypeChange("BOTH")}
                  className={styles.radioInput}
                />
                <span className={styles.radioText}>{t("Both")}</span>
              </label>
            </div>
          </div>

          {/* Basic Information */}
          <div className={styles.formSection}>
            <h3 className={styles.sectionTitle}>{t("Basic Information")}</h3>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>
                  {t("Name")} <span className={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className={`${styles.input} ${
                    errors.name ? styles.error : ""
                  }`}
                  placeholder={t("Enter entity name")}
                />
                {errors.name && (
                  <span className={styles.errorMessage}>{errors.name}</span>
                )}
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>
                  {t("Phone")} <span className={styles.required}>*</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className={`${styles.input} ${
                    errors.phone ? styles.error : ""
                  }`}
                  placeholder={t("Enter phone number")}
                  dir="rtl"
                />
                {errors.phone && (
                  <span className={styles.errorMessage}>{errors.phone}</span>
                )}
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>{t("Email")}</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`${styles.input} ${
                    errors.email ? styles.error : ""
                  }`}
                  placeholder={t("Enter email address")}
                />
                {errors.email && (
                  <span className={styles.errorMessage}>{errors.email}</span>
                )}
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>
                  {t("Address")} <span className={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  className={`${styles.input} ${
                    errors.address ? styles.error : ""
                  }`}
                  placeholder={t("Enter address")}
                />
                {errors.address && (
                  <span className={styles.errorMessage}>{errors.address}</span>
                )}
              </div>
            </div>
          </div>

          {/* Financial Information */}
          <div className={styles.formSection}>
            <h3 className={styles.sectionTitle}>
              {t("Financial Information")}
            </h3>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>{t("Initial Balance")}</label>
                <div className={styles.inputWrapper}>
                  <input
                    type="number"
                    name="initialBalance"
                    value={formData.initialBalance}
                    onChange={handleInputChange}
                    className={`${styles.input} ${
                      errors.initialBalance ? styles.error : ""
                    }`}
                    placeholder="0.00"
                    step="0.01"
                  />
                  <span className={styles.currency}>{t("EGP")}</span>
                </div>
                {errors.initialBalance && (
                  <span className={styles.errorMessage}>
                    {errors.initialBalance}
                  </span>
                )}
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>{t("Credit Limit")}</label>
                <div className={styles.inputWrapper}>
                  <input
                    type="number"
                    name="creditLimit"
                    value={formData.creditLimit}
                    onChange={handleInputChange}
                    className={`${styles.input} ${
                      errors.creditLimit ? styles.error : ""
                    }`}
                    placeholder="0.00"
                    step="0.01"
                  />
                  <span className={styles.currency}>{t("EGP")}</span>
                </div>
                {errors.creditLimit && (
                  <span className={styles.errorMessage}>
                    {errors.creditLimit}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className={styles.formActions}>
          <button
            type="button"
            onClick={handleCancel}
            className={styles.cancelButton}
            disabled={loading}
          >
            <FaTimes /> {t("Cancel")}
          </button>
          <button
            type="submit"
            className={styles.submitButton}
            disabled={loading}
          >
            {loading ? (
              <>
                <span className={styles.spinner}></span>
                {t("Saving...")}
              </>
            ) : (
              <>
                <FaSave /> {isEditMode ? t("Update") : t("Save")}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default BusinessEntityForm;
